import React, { useState, useEffect, useRef } from 'react'
import { Search, Filter, X } from 'lucide-react'
import { formatGuestName, formatTheme } from '../utils/textUtils'
import FilterChips from './FilterChips'

const SearchBar = ({ 
  onSearch, 
  onFilterChange, 
  availableGuests = [], 
  availableGames = [], 
  availableThemes = [],
  initialQuery = '',
  initialFilters = {},
  episodes = [],
  onCategoryFiltersChange
}) => {
  const [query, setQuery] = useState(initialQuery)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    guests: [],
    games: [],
    themes: [],
    ...initialFilters
  })

  // Mettre à jour le state local quand les props changent
  useEffect(() => {
    setQuery(initialQuery)
  }, [initialQuery])

  useEffect(() => {
    setFilters({
      guests: [],
      games: [],
      themes: [],
      ...initialFilters
    })
  }, [initialFilters])

  // Debouncing pour la recherche
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onSearch(query, filters)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, filters])

  const handleQueryChange = (e) => {
    setQuery(e.target.value)
  }

  const handleFilterChange = (filterType, value, isAdding) => {
    const newFilters = { ...filters }
    
    if (isAdding) {
      if (!newFilters[filterType].includes(value)) {
        newFilters[filterType].push(value)
      }
    } else {
      newFilters[filterType] = newFilters[filterType].filter(item => item !== value)
    }
    
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const removeFilter = (filterType, value) => {
    handleFilterChange(filterType, value, false)
  }

  const clearAllFilters = () => {
    const emptyFilters = { guests: [], games: [], themes: [] }
    setFilters(emptyFilters)
    onFilterChange(emptyFilters)
  }

  const hasActiveFilters = filters.guests.length > 0 || filters.games.length > 0 || filters.themes.length > 0

  return (
    <div className="search-bar-container">
      {/* Barre de recherche principale */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10 text-text-muted" 
            size={20} 
          />
          <input
            id="search-input"
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Rechercher dans les épisodes, invités, jeux..."
            className="input-field text-base h-14 w-full pr-6"
            style={{ paddingLeft: '4rem' }}
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn flex-shrink-0 h-14 ${
            showFilters 
              ? 'btn-primary' 
              : 'btn-secondary'
          }`}
          aria-label="Filtres"
        >
          <Filter size={18} />
          <span className="hidden sm:inline ml-2">Filtres</span>
        </button>
      </div>

      {/* Filtres actifs */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-3 mt-4">
          {filters.guests.map(guest => (
            <span key={guest} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm border border-blue-500/30">
              <span className="font-medium">Invité :</span> {formatGuestName(guest)}
              <button
                onClick={() => removeFilter('guests', guest)}
                className="ml-1 rounded-full p-1 transition-colors"
                aria-label={`Supprimer ${guest} des filtres`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          
          {filters.games.map(game => (
            <span key={game} className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 rounded-full text-sm border border-green-500/30">
              <span className="font-medium">Jeu :</span> {game}
              <button
                onClick={() => removeFilter('games', game)}
                className="ml-1 rounded-full p-1 transition-colors"
                aria-label={`Supprimer ${game} des filtres`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          
          {filters.themes.map(theme => (
            <span key={theme} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm border border-purple-500/30">
              <span className="font-medium">Thème :</span> {formatTheme(theme)}
              <button
                onClick={() => removeFilter('themes', theme)}
                className="ml-1 rounded-full p-1 transition-colors"
                aria-label={`Supprimer ${theme} des filtres`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          
          <button
            onClick={clearAllFilters}
            className="btn btn-secondary text-sm"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Panneau de filtres avancés */}
      {showFilters && (
        <div className="mt-4 p-3 bg-background-card rounded-lg border border-border-light backdrop-filter backdrop-blur-md">
          <h3 className="font-medium text-text-primary mb-4 text-center text-base">Filtres avancés</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Filtres invités */}
            <div>
              <label className="block text-xs font-medium text-text-primary mb-4 text-center">
                Invités
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleFilterChange('guests', e.target.value, true)
                    e.target.value = ''
                  }
                }}
                className="input-field text-sm"
                style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">Sélectionner un invité</option>
                {availableGuests
                  .filter(guest => !filters.guests.includes(guest))
                  .map(guest => (
                    <option key={guest} value={guest}>{formatGuestName(guest)}</option>
                  ))}
              </select>
            </div>

            {/* Filtres jeux */}
            <div>
              <label className="block text-xs font-medium text-text-primary mb-4 text-center">
                Jeux
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleFilterChange('games', e.target.value, true)
                    e.target.value = ''
                  }
                }}
                className="input-field text-sm"
                style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">Sélectionner un jeu</option>
                {availableGames
                  .filter(game => !filters.games.includes(game))
                  .map(game => (
                    <option key={game} value={game}>{game}</option>
                  ))}
              </select>
            </div>

            {/* Filtres thèmes */}
            <div>
              <label className="block text-xs font-medium text-text-primary mb-4 text-center">
                Thèmes
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleFilterChange('themes', e.target.value, true)
                    e.target.value = ''
                  }
                }}
                className="input-field text-sm"
                style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">Sélectionner un thème</option>
                {availableThemes
                  .filter(theme => !filters.themes.includes(theme))
                  .map(theme => (
                    <option key={theme} value={theme}>{formatTheme(theme)}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Filtres par catégories */}
          <div className="mt-4 border-t border-border-light">
            <FilterChips
              episodes={episodes}
              onFiltersChange={onCategoryFiltersChange}
              selectedMoods={initialFilters.moods || []}
              selectedContentTypes={initialFilters.contentTypes || []}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchBar 